import { authenticatedApi, API_BASE_URL } from '../apiUtils';

// Cache constants
const CACHE_TTL = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

// In-memory cache (storing ArrayBuffer for maximum flexibility)
let memoryCache = null;
let memoryCacheExpiry = 0;

/**
 * Retrieves the CV template document with in-memory caching
 * @param {boolean} asArrayBuffer - Whether to return the template as an ArrayBuffer (for DocxTemplater)
 * @returns {Promise<ArrayBuffer|Blob>} - The CV template as an ArrayBuffer or Blob
 */
export const getCvTemplate = async (asArrayBuffer = true) => {
  try {
    // Check in-memory cache first
    if (memoryCache && memoryCacheExpiry > Date.now()) {
      // Return the cached ArrayBuffer or convert to Blob if needed
      if (asArrayBuffer) {
        return memoryCache;
      } else {
        // Convert cached ArrayBuffer to Blob when needed
        return new Blob([memoryCache], { 
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
        });
      }
    }
    
    // Fetch from API if not in cache or expired
    const response = await authenticatedApi.get(`${API_BASE_URL}/cv/template`);
    
    // Check if response is OK
    if (!response.ok) {
      throw new Error(`Failed to download CV template: ${response.status}`);
    }
    
    // Get content type
    const contentType = response.headers.get('Content-Type') || 
                         'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    
    // Get the blob from the response
    const blob = await response.blob();
    
    // Convert to ArrayBuffer and cache it
    const arrayBuffer = await blob.arrayBuffer();
    
    // Store ArrayBuffer in memory cache (most flexible format)
    memoryCache = arrayBuffer;
    memoryCacheExpiry = Date.now() + CACHE_TTL;
    
    // Return in the requested format
    if (asArrayBuffer) {
      return arrayBuffer;
    } else {
      return new Blob([arrayBuffer], { type: contentType });
    }
  } catch (error) {
    console.error('Error fetching CV template:', error);
    throw error;
  }
};